import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import StatCard from "../../../../parts/cards/StatCard";
import StatBand from "../../income/components/StatBand";
import { hourLabel } from "../busy-times-utils";
import { rangeDates, summarySeries } from "../door-access-utils";

const formatRange = (filters, timeZone) => {
  const { from, to } = rangeDates(filters, timeZone);
  const fmt = (day) => moment.tz(day, "YYYY-MM-DD", timeZone).format("MMM D");
  return from === to ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;
};

/**
 * "Right now" (gym-wide) and "Selected range" cards, each with its sparkline.
 * Denied attempts only shows when there are some (FaciliKey rarely sends them)
 * or while the log is filtered to Denied; the whole card filters the log to
 * Denied and, pressed again, clears the filter.
 */
const LogsStats = ({
  summary,
  filters,
  timeZone,
  hasEvents,
  onToggleDenied,
}) => {
  const { t } = useTranslation("coaching-portal");
  const data = summary.data;
  const noData = !hasEvents || summary.status === "error";
  const value = (number) => (noData ? "–" : number);
  const caption = (text) => (noData ? t("doorAccess.stats.noData") : text);
  const deniedActive = filters.result === "denied";
  const showDenied = deniedActive || (data?.range.deniedAttempts ?? 0) > 0;
  const lines = summarySeries(data, timeZone);
  const rightNow = data?.rightNow;
  const range = data?.range;
  const days = range?.daily?.length ?? 0;

  const rangeLabel = t(`doorAccess.filters.range.${filters.range}`);
  const busiest = rightNow?.busiestHour;
  const attempts = (range?.visits ?? 0) + (range?.deniedAttempts ?? 0);
  const deniedShare = attempts
    ? (((range?.deniedAttempts ?? 0) / attempts) * 100).toFixed(1)
    : "0";

  return (
    <div className="door-stats">
      <StatBand
        kind="snapshot"
        title={t("doorAccess.stats.rightNow")}
        asAt={rightNow?.asAt ? moment.tz(rightNow.asAt, timeZone) : undefined}
        t={t}
      >
        <StatCard
          title={t("doorAccess.stats.inGym.title")}
          tooltipText={t("doorAccess.stats.inGym.help")}
          current={value(rightNow?.inGymLastHour ?? 0)}
          caption={caption(
            busiest
              ? t("doorAccess.stats.inGym.busiest", {
                  hour: hourLabel(busiest.hour),
                })
              : undefined
          )}
          sparklineData={lines.hours.data}
          sparklineLabels={lines.hours.labels}
        />
        <StatCard
          title={t("doorAccess.stats.visitsToday.title")}
          tooltipText={t("doorAccess.stats.visitsToday.help")}
          current={value(rightNow?.visitsToday ?? 0)}
          caption={caption(
            rightNow
              ? t("doorAccess.stats.visitsToday.lastWeek", {
                  weekday: moment.tz(rightNow.asAt, timeZone).format("dddd"),
                  count: rightNow.visitsLastWeekByNow,
                })
              : undefined
          )}
          sparklineData={lines.cumulative.data}
          sparklineLabels={lines.cumulative.labels}
        />
      </StatBand>

      {!noData && (
        <StatBand
          kind="period"
          title={t("doorAccess.stats.selectedRange")}
          periodRangeText={`${rangeLabel} · ${formatRange(filters, timeZone)}`}
          t={t}
        >
          <StatCard
            title={t("doorAccess.stats.visits.title")}
            tooltipText={t("doorAccess.stats.visits.help")}
            current={value(range?.visits ?? 0)}
            caption={caption(
              range
                ? t("doorAccess.stats.visits.previous", {
                    count: days,
                    visits: range.previousVisits.toLocaleString("en-US"),
                  })
                : undefined
            )}
            sparklineData={lines.visits.data}
            sparklineLabels={lines.visits.labels}
          />
          <StatCard
            title={t("doorAccess.stats.unique.title")}
            tooltipText={t("doorAccess.stats.unique.help")}
            current={value(range?.uniqueMembers ?? 0)}
            caption={caption(
              range?.membersWithAccess
                ? t("doorAccess.stats.unique.ofActive", {
                    count: range.membersWithAccess,
                  })
                : undefined
            )}
            sparklineData={lines.unique.data}
            sparklineLabels={lines.unique.labels}
          />
          {showDenied && (
            <StatCard
              title={t("doorAccess.stats.denied.title")}
              tooltipText={t("doorAccess.stats.denied.help")}
              current={value(range?.deniedAttempts ?? 0)}
              caption={caption(
                deniedActive
                  ? t("doorAccess.stats.denied.filtered")
                  : t("doorAccess.stats.denied.share", { percent: deniedShare })
              )}
              active={deniedActive}
              onClick={noData ? undefined : onToggleDenied}
            />
          )}
          {(range?.emergencyOpens ?? 0) > 0 && (
            <StatCard
              title={t("doorAccess.stats.emergency.title")}
              tooltipText={t("doorAccess.stats.emergency.help")}
              current={range.emergencyOpens}
            />
          )}
        </StatBand>
      )}
    </div>
  );
};

export default LogsStats;
